import React, { useState, useRef, useEffect } from 'react';
import { Volume2 } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const SOUNDS = [
  { id: 'rain', url: 'https://cdn.freesound.org/previews/531/531947_6142149-lq.mp3' },
  { id: 'fireplace', url: 'https://cdn.freesound.org/previews/380/380204_1370675-lq.mp3' },
  { id: 'night', url: 'https://cdn.freesound.org/previews/365/365920_2188818-lq.mp3' },
  { id: 'ocean', url: 'https://cdn.freesound.org/previews/467/467953_9564583-lq.mp3' },
];

const AmbientSounds = () => {
  const { t } = useLanguage();
  const [showMenu, setShowMenu] = useState(false);
  const [activeSound, setActiveSound] = useState(null);
  const [volume, setVolume] = useState(0.3);
  const audioRef = useRef(null);

  useEffect(() => {
    return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  }, []);

  const playSound = (soundId) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }

    if (activeSound === soundId) { setActiveSound(null); return; }

    const sound = SOUNDS.find(s => s.id === soundId);
    if (!sound) return;

    const audio = new Audio(sound.url);
    audio.loop = true;
    audio.volume = volume;
    audio.play().catch(() => {});
    audioRef.current = audio;
    setActiveSound(soundId);
  };

  const updateVolume = (v) => {
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
  };

  const stopAll = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setActiveSound(null);
  };

  return (
    <div className="relative" data-testid="ambient-sounds">
      <Button
        data-testid="ambient-sounds-btn"
        variant="ghost"
        size="sm"
        onClick={() => setShowMenu(!showMenu)}
        className={`rounded-full px-3 ${activeSound ? 'text-gold bg-gold/10' : ''}`}
      >
        <Volume2 className="w-4 h-4" />
      </Button>

      {showMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
          <div className="absolute right-0 bottom-12 z-50 bg-card border border-border rounded-xl shadow-xl p-3 min-w-[200px]" data-testid="ambient-menu">
            <p className="text-xs font-bold text-foreground mb-2 px-1">{t('ambient.title')}</p>
            <div className="space-y-1">
              {SOUNDS.map(s => (
                <button
                  key={s.id}
                  data-testid={`ambient-${s.id}`}
                  onClick={() => playSound(s.id)}
                  className={`w-full px-3 py-2 rounded-lg text-sm text-left transition-colors flex items-center gap-2 ${activeSound === s.id ? 'bg-gold/20 text-gold font-medium' : 'hover:bg-muted text-foreground'}`}
                >
                  <span className={`w-2 h-2 rounded-full ${activeSound === s.id ? 'bg-gold animate-pulse' : 'bg-muted-foreground/30'}`} />
                  {t(`ambient.${s.id}`)}
                </button>
              ))}
              <button
                data-testid="ambient-off"
                onClick={stopAll}
                className="w-full px-3 py-2 rounded-lg text-sm text-left text-muted-foreground hover:bg-muted transition-colors"
              >
                {t('ambient.off')}
              </button>
            </div>
            {activeSound && (
              <div className="mt-2 px-1">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={volume}
                  onChange={(e) => updateVolume(parseFloat(e.target.value))}
                  className="w-full h-1 accent-gold"
                  data-testid="ambient-volume"
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AmbientSounds;
